/**
 * Custom hook for tracking page views with Amplitude
 * Automatically tracks when the user navigates to different pages
 */

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { logEvent, PAGE_EVENTS } from '../utils/amplitude';
import { useAppSelector } from '../redux/hooks';

/**
 * Hook to automatically track page views when route changes
 */
export const useAmplitudePageTracking = (): void => {
  const location = useLocation();
  const previousPathRef = useRef<string>('');
  const userProfile = useAppSelector((state) => state.userReducer.userProfile);
  const permissions = useAppSelector((state) => state.userReducer.permissions);

  useEffect(() => {
    const currentPath = location.pathname;
    const previousPath = previousPathRef.current;

    // Get a human-readable page name from the path
    const pageName = getPageNameFromPath(currentPath);
    
    // Get user role if available
    const userRole = permissions.length > 0 
      ? permissions.map(p => p.name).join(', ') 
      : undefined;

    // Track the page view
    logEvent(PAGE_EVENTS.PAGE_VIEWED, {
      page_name: pageName,
      page_path: currentPath,
      previous_page: previousPath || undefined,
      user_role: userRole,
      is_authenticated: !!userProfile.token,
    });

    // Update the previous path for next navigation
    previousPathRef.current = currentPath;
  }, [location.pathname, userProfile.token, permissions]);
};

/**
 * Convert route path to human-readable page name
 */
const getPageNameFromPath = (path: string): string => {
  // Remove leading slash and split by slash
  const segments = path.replace(/^\//, '').split('/');
  
  // Map common routes to readable names
  const routeMap: Record<string, string> = {
    '': 'Home',
    'dashboard': 'Dashboard',
    'leads': 'Leads',
    'waitlist': 'Waitlist',
    'projects': 'Projects',
    'project-create': 'Create Project',
    'insurance': 'Insurance Companies',
    'materials': 'Materials',
    'material-create': 'Create Material',
    'users': 'Users',
    'customers': 'Customers',
    'notifications': 'Notifications',
    'sales-estimate-calculator': 'Sales Estimate Calculator',
    'analytics-dashboard': 'Analytics Dashboard',
    'profile': 'Profile',
    'my-profile': 'My Profile',
    'self-reset-password': 'Reset Password',
    'forgot-password': 'Forgot Password',
    'logout': 'Logout',
    'map': 'Map View',
  };

  // Get the first segment
  const mainSegment = segments[0] || '';
  
  // Check if it's a known route
  if (routeMap[mainSegment]) {
    // If there are more segments, it might be a detail page
    if (segments.length > 1) {
      // Special cases for detail pages
      if (mainSegment === 'projects') {
        return segments[1] === 'manage-project' ? 'Manage Project' : 'Project Details';
      }
      if (mainSegment === 'customers') {
        return segments[1] === 'customer-project-create' ? 'Create Customer Project' : 'Customer Details';
      }
      // Generic detail page
      return `${routeMap[mainSegment]} Details`;
    }
    return routeMap[mainSegment];
  }

  // For unknown routes, convert path to title case
  return segments
    .map(segment => segment.split('-').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' '))
    .join(' > ') || 'Unknown Page';
};

export default useAmplitudePageTracking;
